import { isHebrewText } from "./isHebrewText";

/**
 * Thin wrapper over the modern, non-deprecated
 * `google.maps.places.AutocompleteSuggestion` API that returns predictions in
 * the same `{ place_id, description }` shape the legacy
 * `AutocompleteService.getPlacePredictions` produced. Lets call-sites that drive
 * their own dropdown (e.g. the Settings region/address pickers) swap off the
 * deprecated service without changing their UI. See
 * MD/PLACES_AUTOCOMPLETE_MIGRATION_PLAN.md.
 */
export interface SimplePlacePrediction {
  place_id: string;
  description: string;
}

/** Region-ish primary types — modern equivalent of the legacy `types: ['(regions)']`. */
export const REGION_PRIMARY_TYPES = [
  "locality",
  "administrative_area_level_1",
  "administrative_area_level_2",
  "country",
  "postal_code",
];

/** Street-address primary types — modern equivalent of the legacy `types: ['address']`. */
export const ADDRESS_PRIMARY_TYPES = ["street_address", "premise", "subpremise"];

/** City-ish primary types — for a city picker (locality first, with fallbacks). */
export const CITY_PRIMARY_TYPES = ["locality", "postal_town", "administrative_area_level_2"];

export async function fetchPlacePredictions(
  input: string,
  options?: {
    includedPrimaryTypes?: string[];
    sessionToken?: any;
    /** ISO-3166 region/country codes to restrict suggestions to (e.g. ["il"]). */
    includedRegionCodes?: string[];
  }
): Promise<SimplePlacePrediction[]> {
  const places: any = (window as any).google?.maps?.places;
  if (!places?.AutocompleteSuggestion || !input.trim()) return [];

  const request: any = { input };
  if (options?.includedPrimaryTypes?.length) {
    request.includedPrimaryTypes = options.includedPrimaryTypes;
  }
  if (options?.includedRegionCodes?.length) {
    // Restrict suggestions to these countries — a city outside the selected
    // country will simply not be offered.
    request.includedRegionCodes = options.includedRegionCodes.map((c) => c.toLowerCase());
  }
  if (options?.sessionToken) request.sessionToken = options.sessionToken;
  // Per-request language decided from the text the user actually typed — not
  // from workspace/account country — mirroring PlaceAutocompleteInput.
  // Explicit in both directions — omitting it for a Latin query falls through
  // to the language the Maps script was loaded in (the UI language), which
  // returns one suggestion in two languages: an English street over a Hebrew
  // city. See PlaceAutocompleteInput for the reported case.
  request.language = isHebrewText(input) ? "iw" : "en";

  try {
    const { suggestions } =
      await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return (suggestions ?? [])
      .map((s: any) => s.placePrediction)
      .filter(Boolean)
      .map((pp: any) => ({
        place_id: pp.placeId,
        description: pp.text?.text ?? "",
      }));
  } catch {
    return [];
  }
}
